import './DebugPage.sass';

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, FormItem, Header, Input, ScrollView, Select } from '$uikit';
import { MetaTags } from '$shared/components';
import { mainActions } from '$store/main';
import {
  getApiUrl,
  getForceGmailOAuth,
  getGoogleClientId,
  getRegistrationToken,
  getStorageItem,
  getTelegramBotId,
  getToken,
  getTokenRaw,
  setApiUrl,
  setForceGmailOAuth,
  setGoogleClientId,
  setRegistrationToken,
  setStorageItem,
  setTelegramBotId,
  setTokenRaw,
} from '$utils';
import {
  AuthMethod,
  getForcedMethod,
  setForcedMethod,
  getDetectInfo,
} from 'services/telegramLogin';

const HARD_PRESETS_API = [
  'https://localhost:8443/',
  'https://api.tonplace.host/',
  'https://api.tonplace.net/',
];

const COUNTRY_CODE_PRESETS = [
  { code: 'RU', label: 'Russia (RU)' },
  { code: 'US', label: 'United States (US)' },
  { code: 'BR', label: 'Brazil (BR)' },
  { code: 'MX', label: 'Mexico (MX)' },
  { code: 'UA', label: 'Ukraine (UA)' },
  { code: 'KZ', label: 'Kazakhstan (KZ)' },
];

const getCookieBase = () => {
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isSecure = window.location.protocol === 'https:';

  if (isLocalhost) return ['Path=/'];

  const base = ['Domain=.tonplace.host', 'Path=/'];
  if (isSecure) base.push('Secure', 'SameSite=None');
  return base;
};

const setCookie = (
  name: string,
  value: string | number | boolean,
  maxAge: number = 3600,
): void => {
  const cookieBase = getCookieBase();
  document.cookie = [`${name}=${value}`, ...cookieBase, `Max-Age=${maxAge}`].join('; ');
  console.log('Set cookie:', name, '=', value, '| Cookie string:', document.cookie);
};

const deleteCookie = (name: string): void => {
  const cookieBase = getCookieBase();
  document.cookie = [`${name}=`, ...cookieBase, 'Max-Age=0'].join('; ');
};

const getCookie = (name: string): string => {
  const safe = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const m = document.cookie.match(new RegExp(`(?:^|; )${safe}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : '';
};

const getCookieBool = (name: string): boolean => {
  return ['1', 'true', 'on', 'enabled'].includes(getCookie(name).toLowerCase());
};

export function DebugPage() {
  const dispatch = useDispatch();
  const [apiUrl, apiUrlSet] = useState('');
  const [telegramBotId, telegramBotIdSet] = useState(0);
  const [googleClientId, googleClientIdSet] = useState('');
  const [forceGmailOAuth, forceGmailOAuthSet] = useState(false);
  const [registrationToken, registrationTokenSet] = useState('');
  const [tokenRaw, tokenRawSet] = useState('');
  const [tokenView, tokenViewSet] = useState<{ userId: number; token?: string }>({
    userId: 0,
    token: '',
  });

  const [skipCaptcha, skipCaptchaSet] = useState(false);
  const [tempEmail, tempEmailSet] = useState(false);
  const [emailCode, emailCodeSet] = useState('');
  const [countryCode, countryCodeSet] = useState('');

  const [tgAuthMethod, tgAuthMethodSet] = useState<AuthMethod | ''>('');
  const [tgDetectInfo, tgDetectInfoSet] = useState<ReturnType<typeof getDetectInfo> | null>(null);

  useEffect(() => {
    apiUrlSet(getApiUrl());
    telegramBotIdSet(getTelegramBotId());
    googleClientIdSet(getGoogleClientId());
    forceGmailOAuthSet(getForceGmailOAuth());
    registrationTokenSet(getRegistrationToken() || '');
    tokenRawSet(getTokenRaw() || '');
    tokenViewSet(getToken());

    skipCaptchaSet(getCookieBool('X-Skip-Captcha'));
    tempEmailSet(getCookieBool('X-Temp-Email'));
    emailCodeSet(getCookie('X-Email-Code'));
    countryCodeSet(getStorageItem('countryCode') || '');

    tgAuthMethodSet(getForcedMethod() || '');
    tgDetectInfoSet(getDetectInfo());
  }, []);

  const handleSave = useCallback(() => {
    setApiUrl(apiUrl);
    setTelegramBotId(telegramBotId);
    setGoogleClientId(googleClientId);
    setForceGmailOAuth(forceGmailOAuth);
    setRegistrationToken(registrationToken);
    setTokenRaw(tokenRaw);

    if (countryCode) {
      setStorageItem('countryCode', countryCode);
      dispatch(mainActions.setCountryCode(countryCode));
    }

    setCookie('X-Skip-Captcha', skipCaptcha ? 1 : 0);
    setCookie('X-Temp-Email', tempEmail ? 1 : 0);

    if (emailCode && emailCode.trim() !== '') {
      setCookie('X-Email-Code', emailCode.trim());
    } else {
      deleteCookie('X-Email-Code');
    }

    setForcedMethod(tgAuthMethod ? (tgAuthMethod as AuthMethod) : null);

    console.info('saved!');
  }, [
    apiUrl,
    telegramBotId,
    googleClientId,
    forceGmailOAuth,
    registrationToken,
    tokenRaw,
    countryCode,
    skipCaptcha,
    tempEmail,
    emailCode,
    tgAuthMethod,
    dispatch,
  ]);

  return (
    <>
      <MetaTags title="Debug" />
      <Header showBackButton>Debug</Header>
      <ScrollView>
        <FormItem title={'API URL'}>
          <Input
            value={apiUrl}
            onChange={(e) => apiUrlSet(e.target.value)}
            placeholder="https://api.tonplace.net/"
          />
          <Select onChange={(e) => apiUrlSet(e.target.value)} value="">
            <option value="" disabled>
              — presets —
            </option>
            {HARD_PRESETS_API.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </FormItem>

        <FormItem title={'Telegram Bot ID'}>
          <Input
            value={telegramBotId.toString()}
            onChange={(e) => telegramBotIdSet(Number(e.target.value))}
            placeholder="telegram_bot_id"
          />
        </FormItem>

        <FormItem title={'Google Client ID'}>
          <Input
            value={googleClientId}
            onChange={(e) => googleClientIdSet(e.target.value)}
            placeholder="google_client_id"
          />
        </FormItem>

        <FormItem title={'Force Gmail OAuth'}>
          <Checkbox
            checked={forceGmailOAuth}
            onClick={() => forceGmailOAuthSet(!forceGmailOAuth)}
          >
            Включить
          </Checkbox>
        </FormItem>

        <FormItem title={'Registration token'}>
          <Input
            value={registrationToken}
            onChange={(e) => registrationTokenSet(e.target.value)}
            placeholder="registration_token"
          />
        </FormItem>

        <FormItem title={'Token Raw'}>
          <Input
            value={tokenRaw}
            onChange={(e) => tokenRawSet(e.target.value)}
            placeholder="userId,token"
          />
        </FormItem>

        <FormItem title={'Token View'}>
          {tokenView ? JSON.stringify(tokenView) : '—'}
        </FormItem>

        <FormItem title={'X-Skip-Captcha'}>
          <Checkbox checked={skipCaptcha} onClick={() => skipCaptchaSet(!skipCaptcha)}>
            Включить
          </Checkbox>
        </FormItem>

        <FormItem title={'X-Temp-Email'}>
          <Checkbox checked={tempEmail} onClick={() => tempEmailSet(!tempEmail)}>
            Включить
          </Checkbox>
        </FormItem>

        <FormItem title={'X-Email-Code'}>
          <Input
            value={emailCode}
            onChange={(e) => emailCodeSet(e.target.value)}
            placeholder="Введите временный код"
          />
        </FormItem>

        <FormItem title={'Country Code'}>
          <Input
            value={countryCode}
            onChange={(e) => countryCodeSet(e.target.value)}
            placeholder="RU, US, BR, MX, etc."
          />
          <Select onChange={(e) => countryCodeSet(e.target.value)} value="">
            <option value="" disabled>
              — presets —
            </option>
            {COUNTRY_CODE_PRESETS.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormItem>

        <FormItem title={'Telegram Auth Method (force)'}>
          <Select
            onChange={(e) => tgAuthMethodSet(e.target.value as AuthMethod | '')}
            value={tgAuthMethod}
          >
            <option value="">auto (detect)</option>
            <option value="oidc">oidc</option>
            <option value="widget">widget</option>
            <option value="link">link</option>
          </Select>
        </FormItem>

        <FormItem title={'Telegram Auth Detect Info'}>
          <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {tgDetectInfo ? JSON.stringify(tgDetectInfo, null, 2) : 'loading...'}
          </pre>
        </FormItem>

        <Button onClick={handleSave}>Сохранить</Button>
      </ScrollView>
    </>
  );
}
